import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { compressImage, isImageFile, formatFileSize } from '@/utils/imageCompression';

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

const ALLOWED_FILE_TYPES = [
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/plain',
  'text/csv',
  'application/zip',
  'application/x-zip-compressed',
  'application/json',
  'application/xml',
  'text/xml',
  'application/rtf',
  // Video
  'video/mp4',
  'video/quicktime',
  // Audio
  'audio/webm',
  'audio/webm;codecs=opus',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg',
  'audio/wav',
  'audio/wave',
  'audio/x-wav',
  'audio/aac',
  'audio/mp4a-latm',
  'audio/x-aac',
  'audio/x-hx-aac-adts',
  'audio/m4a',
  'audio/x-m4a',
  'audio/3gpp',
  'audio/amr',
  // Generic fallback for native file pickers
  'application/octet-stream',
];

const inferMimeTypeFromName = (name: string): string => {
  const ext = name.split(".").pop()?.toLowerCase();
  switch (ext) {
    // Audio
    case "wav": return "audio/wav";
    case "aac": return "audio/aac";
    case "m4a": return "audio/x-m4a";
    case "3gp": case "3gpp": return "audio/3gpp";
    case "amr": return "audio/amr";
    case "webm": return "audio/webm";
    case "ogg": return "audio/ogg";
    case "mp3": return "audio/mpeg";
    // Documents
    case "pdf": return "application/pdf";
    case "doc": return "application/msword";
    case "docx": return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
    case "xls": return "application/vnd.ms-excel";
    case "xlsx": return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
    case "ppt": return "application/vnd.ms-powerpoint";
    case "pptx": return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
    case "txt": return "text/plain";
    case "csv": return "text/csv";
    case "json": return "application/json";
    case "xml": return "application/xml";
    case "rtf": return "application/rtf";
    case "zip": return "application/zip";
    // Images
    case "jpg": case "jpeg": return "image/jpeg";
    case "png": return "image/png";
    case "gif": return "image/gif";
    case "webp": return "image/webp";
    case "heic": return "image/heic";
    case "heif": return "image/heif";
    // Video
    case "mp4": return "video/mp4";
    case "mov": return "video/quicktime";
    default: return "application/octet-stream";
  }
};

const fileSchema = z.object({
  size: z.number().max(MAX_FILE_SIZE, 'File size must be less than 10MB'),
  type: z.string().refine(
    (type) => {
      // Check for exact match or match base MIME type (handle codec suffixes like audio/webm;codecs=opus)
      const baseType = type.split(';')[0];
      return ALLOWED_FILE_TYPES.includes(type) || ALLOWED_FILE_TYPES.includes(baseType);
    },
    'File type not supported'
  ),
});

export const useFileUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const { toast } = useToast();

  const uploadFile = async (file: File, chatId: string): Promise<string | null> => {
    setUploading(true);

    try {
      let fileToUpload: File | Blob = file;
      const originalSize = file.size;

      // Compress image files before upload
      if (isImageFile(file)) {
        toast({
          title: 'Compressing image...',
          description: `Original size: ${formatFileSize(originalSize)}`,
        });

        const compressedBlob = await compressImage(file);
        const compressedSize = compressedBlob.size;
        
        // Only use compressed version if it's smaller
        if (compressedSize < originalSize) {
          fileToUpload = compressedBlob;
          const reduction = Math.round(((originalSize - compressedSize) / originalSize) * 100);
          
          toast({
            title: 'Image compressed!',
            description: `Reduced by ${reduction}% (${formatFileSize(originalSize)} → ${formatFileSize(compressedSize)})`,
          });
        } else {
          toast({
            title: 'Using original image',
            description: 'Compression did not reduce file size',
          });
        }
      }

      // Validate file
      const effectiveType = (file.type || inferMimeTypeFromName(file.name)).trim();

      console.log('[useFileUpload] Validating file:', {
        name: file.name,
        type: file.type,
        effectiveType,
        size: fileToUpload.size,
      });

      const validation = fileSchema.safeParse({
        size: fileToUpload.size,
        type: effectiveType,
      });

      if (!validation.success) {
        const error = validation.error.issues[0];
        console.error('[useFileUpload] Validation failed:', error.message, {
          type: file.type,
          effectiveType,
        });
        toast({
          title: 'Invalid file',
          description: error.message,
          variant: 'destructive',
        });
        return null;
      }

      // Create unique file name - handle audio files with proper extension detection
      let fileExt = file.name.split('.').pop();
      if (!fileExt || fileExt === file.name) {
        // No extension in filename - derive from MIME type
        const mimeBase = (file.type || inferMimeTypeFromName(file.name)).split(';')[0];
        if (mimeBase.includes('mp4a')) fileExt = 'aac';
        else if (mimeBase.includes('aac')) fileExt = 'aac';
        else if (mimeBase.includes('m4a') || mimeBase.includes('x-m4a')) fileExt = 'm4a';
        else if (mimeBase.includes('3gpp')) fileExt = '3gp';
        else if (mimeBase.includes('amr')) fileExt = 'amr';
        else if (mimeBase.includes('webm')) fileExt = 'webm';
        else if (mimeBase.includes('ogg')) fileExt = 'ogg';
        else if (mimeBase.includes('mpeg')) fileExt = 'mp3';
        else if (mimeBase.includes('mp4')) fileExt = 'mp4';
        else fileExt = 'bin';
      }
      const fileName = `${chatId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${isImageFile(file) ? 'jpg' : fileExt}`;

      console.log('[useFileUpload] Uploading to:', fileName);

      // Upload to Supabase Storage
      toast({
        title: 'Uploading...',
        description: `Size: ${formatFileSize(fileToUpload.size)}`,
      });

      const { data, error } = await supabase.storage
        .from('chat-attachments')
        .upload(fileName, fileToUpload, {
          cacheControl: '3600',
          upsert: false,
          // Use base MIME type; some platforms may provide empty/codec-suffixed types.
          contentType: (file.type || inferMimeTypeFromName(file.name)).split(';')[0],
        });

      if (error) {
        console.error('[useFileUpload] Upload error:', error);
        throw error;
      }

      // NOTE: Do NOT create a signed URL here.
      // In a private bucket, signed URL creation can fail if read policies aren't in place yet.
      // We only store the path reference and generate signed URLs later when we need to display/download.

      toast({
        title: 'Upload complete!',
        description: 'File ready to send',
      });

      // Store the path instead of signed URL so we can regenerate URLs as needed
      // The path format is: chat-attachments/chatId/filename
      return `attachment:${data.path}`;
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast({
        title: 'Upload failed',
        description: error.message || 'Failed to upload file',
        variant: 'destructive',
      });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const getSignedUrl = async (attachmentPath: string): Promise<string | null> => {
    try {
      // Handle legacy public URLs
      if (attachmentPath.startsWith('http')) {
        return attachmentPath;
      }

      // Handle new path format
      const path = attachmentPath.replace('attachment:', '');
      
      const { data, error } = await supabase.storage
        .from('chat-attachments')
        .createSignedUrl(path, 3600); // 1 hour expiry for viewing

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      return null;
    }
  };

  const getFileType = (url: string): 'image' | 'video' | 'audio' | 'document' => {
    const cleanUrl = url.replace('attachment:', '');
    const extension = cleanUrl.split('.').pop()?.toLowerCase()?.split('?')[0];
    
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'heic', 'heif'].includes(extension || '')) {
      return 'image';
    }
    
    if (['mp4', 'mov'].includes(extension || '')) {
      return 'video';
    }
    
    if (['webm', 'mp3', 'ogg', 'm4a', 'wav', 'aac', '3gp', 'amr'].includes(extension || '')) {
      return 'audio';
    }
    
    return 'document';
  };

  const uploadMultipleFiles = async (files: File[], chatId: string): Promise<string[]> => {
    const uploadedUrls: string[] = [];
    setUploading(true);
    
    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
        const url = await uploadFile(files[i], chatId);
        if (url) {
          uploadedUrls.push(url);
        }
      }
      
      return uploadedUrls;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return {
    uploadFile,
    uploadMultipleFiles,
    uploading,
    uploadProgress,
    getFileType,
    getSignedUrl,
  };
};
